/**
 * The Line menu: the marks that run between two notes rather than sitting on one.
 *
 * Every item here needs two notes selected, not one, because that is what a line is -- a
 * slur, a hairpin, a glissando, a pedal bracket and an octave sign all have two ends and
 * nowhere to put the second one on a single note. So the whole menu greys out together on a
 * selection of one, which says why before the click rather than after it.
 *
 * Nothing here is ticked. A line is added rather than switched on: a passage can carry a
 * slur and a hairpin and an octave sign at once, and each click writes another one, so
 * there is no state for a tick to show.
 */

import { marks, selection as select } from "@app/score-editor";
import type { OctaveShift } from "@app/score-editor";
import type { ScoreActions } from "./score-actions";
import { RULE } from "./score-menu-model";
import type { Menu, MenuEntry } from "./score-menu-model";

/** The four octave signs, with what each one says when it is read aloud. */
const OCTAVES: readonly { kind: OctaveShift; label: string }[] = [
  { kind: "8va", label: "8va, an octave up" },
  { kind: "8vb", label: "8vb, an octave down" },
  { kind: "15ma", label: "15ma, two octaves up" },
  { kind: "15mb", label: "15mb, two octaves down" },
];

export function lineMenu(actions: ScoreActions): Menu {
  const { score, selection } = actions;
  // A line is drawn between the first and last selected note of each part, so the count that
  // matters is how many the selection actually holds, not how many bars it looks as if it covers.
  const single = select.selectedLocations(score, selection).length < 2;

  const octaves: MenuEntry[] = OCTAVES.map((entry) => ({
    label: entry.label,
    disabled: single,
    run: () => actions.edit(marks.octave(score, selection, entry.kind)),
  }));

  return {
    title: "Line",
    items: [
      {
        label: "Slur",
        disabled: single,
        run: () => actions.edit(marks.slur(score, selection)),
      },
      {
        label: "Slur, below the notes",
        disabled: single,
        run: () => actions.edit(marks.slur(score, selection, "below")),
      },
      RULE,
      {
        label: "Crescendo",
        disabled: single,
        run: () => actions.edit(marks.hairpin(score, selection, "crescendo")),
      },
      {
        label: "Diminuendo",
        disabled: single,
        run: () => actions.edit(marks.hairpin(score, selection, "diminuendo")),
      },
      RULE,
      {
        label: "Glissando",
        disabled: single,
        run: () => actions.edit(marks.glissando(score, selection)),
      },
      {
        label: "Pedal",
        disabled: single,
        run: () => actions.edit(marks.pedal(score, selection)),
      },
      RULE,
      ...octaves,
      RULE,
      {
        label: "Remove lines and dynamics",
        disabled: selection.events.size === 0,
        run: () => actions.edit(marks.clearMarks(score, selection)),
      },
    ],
  };
}
